import { Building2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { FluentCard } from '@/components/layout/page-header'
import type { DepartmentUsage } from '@/lib/api/types'

interface DepartmentBreakdownProps {
  data: DepartmentUsage[]
  loading?: boolean
}

const COLORS = [
  'from-blue-400 to-blue-600',
  'from-emerald-400 to-green-600',
  'from-violet-400 to-purple-600',
  'from-amber-400 to-orange-600',
  'from-pink-400 to-rose-600',
  'from-cyan-400 to-teal-600',
  'from-red-400 to-red-600',
  'from-indigo-400 to-indigo-600',
]


export function DepartmentBreakdown({ data, loading }: DepartmentBreakdownProps) {
  if (loading) {
    return (
      <FluentCard>
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2.5 rounded-xl bg-gradient-to-br from-emerald-400 to-green-600">
            <Building2 className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Department Usage</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Template usage by department</p>
          </div>
        </div>
        <div className="h-[200px] bg-gray-100 dark:bg-gray-800 rounded-xl animate-pulse" />
      </FluentCard>
    )
  }

  // Calculate totals
  const totalUsage = data.reduce(
    (acc, d) => acc + d.viewCount + d.downloadCount + d.generationCount,
    0
  )

  // Sort by total usage
  const sortedData = [...data].sort((a, b) => {
    const totalA = a.viewCount + a.downloadCount + a.generationCount
    const totalB = b.viewCount + b.downloadCount + b.generationCount
    return totalB - totalA
  })

  const maxTotal = Math.max(
    ...sortedData.map((d) => d.viewCount + d.downloadCount + d.generationCount),
    1
  )

  return (
    <FluentCard>
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2.5 rounded-xl bg-gradient-to-br from-emerald-400 to-green-600">
          <Building2 className="h-5 w-5 text-white" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Department Usage</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">Template usage by department</p>
        </div>
      </div>

      {data.length > 0 ? (
        <div className="space-y-4">
          {sortedData.map((dept, index) => {
            const total = dept.viewCount + dept.downloadCount + dept.generationCount
            const percentage = totalUsage > 0 ? ((total / totalUsage) * 100).toFixed(1) : '0'
            const barWidth = (total / maxTotal) * 100

            return (
              <div key={dept.departmentId} className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div
                      className={cn(
                        'w-3 h-3 rounded-full bg-gradient-to-br',
                        COLORS[index % COLORS.length]
                      )}
                    />
                    <span className="font-medium text-gray-900 dark:text-white">
                      {dept.departmentName}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <span className="text-gray-500 dark:text-gray-400">
                      {total.toLocaleString()} total
                    </span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {percentage}%
                    </span>
                  </div>
                </div>

                {/* Stacked bar */}
                <div className="h-3 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                  <div
                    className="h-full flex transition-all"
                    style={{ width: `${barWidth}%` }}
                  >
                    {/* Views */}
                    {dept.viewCount > 0 && (
                      <div
                        className="h-full bg-blue-500"
                        style={{
                          width: `${(dept.viewCount / total) * 100}%`,
                        }}
                        title={`Views: ${dept.viewCount.toLocaleString()}`}
                      />
                    )}
                    {/* Downloads */}
                    {dept.downloadCount > 0 && (
                      <div
                        className="h-full bg-emerald-500"
                        style={{
                          width: `${(dept.downloadCount / total) * 100}%`,
                        }}
                        title={`Downloads: ${dept.downloadCount.toLocaleString()}`}
                      />
                    )}
                    {/* Generations */}
                    {dept.generationCount > 0 && (
                      <div
                        className="h-full bg-violet-500"
                        style={{
                          width: `${(dept.generationCount / total) * 100}%`,
                        }}
                        title={`Generations: ${dept.generationCount.toLocaleString()}`}
                      />
                    )}
                  </div>
                </div>

                {/* Breakdown */}
                <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-blue-500" />
                    Views: {dept.viewCount.toLocaleString()}
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-emerald-500" />
                    Downloads: {dept.downloadCount.toLocaleString()}
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-violet-500" />
                    Generations: {dept.generationCount.toLocaleString()}
                  </span>
                </div>
              </div>
            )
          })}
        </div>
      ) : (
        <div className="text-center py-8">
          <Building2 className="h-10 w-10 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
          <p className="text-sm text-gray-500 dark:text-gray-400">No department data available</p>
        </div>
      )}
    </FluentCard>
  )
}
